(ns kyber.components.composite.loading
  (:require [applied-science.js-interop :as j]
            [reagent.core :as reagent]
            [kyber.constants :as c]
            [kyber.components.base.text :as text]
            [kyber.components.base.button :as btn]
            [kyber.styles.core :refer [styles->classes gs]]))

(def classes
  (styles->classes
   {:container
    {:width "100%"
     :flex "1 1 auto"
     :display :flex
     :flex-direction :column
     :justify-content :center
     :align-items :center}

    :container-fixed
    {:z-index 1
     :position :fixed
     :top 0
     :left 0
     :bottom 0}

    :container-fill
    {:min-height "500px"}

    :logo
    {:width "auto"
     :height "100%"}

    :logo-wrapper
    {:height (gs [:spacing :p64])
     :margin-bottom (gs [:spacing :p12])}

    :svg-container
    {:position :relative
     :height "70px"
     :margin-top (gs [:spacing :p20])}

    :svg-circle
    {:mix-blend-mode :multiply
     :position :absolute
     :left "-15px"}

    :circle-primary
    {:animation "circle-color 4s infinite alternate"
     :transform-origin "40% 30%"}

    :circle-gray
    {:animation "circle-gray 5s infinite"
     :transform-origin "30% 60%"}

    :circle-ring
    {:animation "circle-ring 7s infinite"
     :transform-origin "40% 40%"}

    "@keyframes circle-color" {"0%" {:transform "rotate(0deg)"}
                               "50%" {:transform "rotate(540deg)"}
                               "100%" {:transform "rotate(0deg)"}}
    "@keyframes circle-gray" {"0%" {:transform "rotate(0deg)"}
                              "50%" {:transform "rotate(360deg)"}
                              "100%" {:transform "rotate(0deg)"}}
    "@keyframes circle-ring" {"0%" {:transform "rotate(0deg)"}
                              "50%" {:transform "rotate(720deg)"}
                              "100%" {:transform "rotate(0deg)"}}}))

(def circle-primary-path
  (str
   "M8.17334993,8.17334993 C19.0711498,-2.72444998 31.9288502,-2.72444998 "
   "42.8266501,8.17334993 C53.72445,19.0711498 53.72445,31.9288502 42.8266501,"
   "42.8266501 C31.9288502,53.72445 19.0711498,53.72445 8.17334993,42.8266501 "
   "C-2.72444998,31.9288502 -2.72444998,19.0711498 8.17334993,8.17334993 Z"))

(def circle-gray-path
  (str
   "M9.45544404,9.45544404 C22.0627028,-3.15181468 36.9372972,-3.15181468 "
   "49.544556,9.45544404 C62.1518147,22.0627028 62.1518147,36.9372972 "
   "49.544556,49.544556 C36.9372972,62.1518147 22.0627028,62.1518147 "
   "9.45544404,49.544556 C-3.15181468,36.9372972 -3.15181468,22.0627028 "
   "9.45544404,9.45544404 Z"))

(def circle-ring-path
  (str
   "M6.25020877,6.25020877 C14.5838205,-2.08340292 24.4161795,-2.08340292 "
   "32.7497912,6.25020877 C41.0834029,14.5838205 41.0834029,24.4161795 "
   "32.7497912,32.7497912 C24.4161795,41.0834029 14.5838205,41.0834029 "
   "6.25020877,32.7497912 C-2.08340292,24.4161795 -2.08340292,14.5838205 "
   "6.25020877,6.25020877 Z"))

(def circle-primary
  [:svg {:class [(:svg-circle classes)
                 (:circle-primary classes)]
         :xmlns "http://www.w3.org/2000/svg"
         :width "30px"
         :height "51px"
         :viewBox "0 0 51 51"}
   [:path {:d circle-primary-path
           :fill (gs [:colors :primary-lite-700])
           :stoke :none}]])

(def circle-gray
  [:svg {:class [(:svg-circle classes)
                 (:circle-gray classes)]
         :xmlns "http://www.w3.org/2000/svg"
         :width "40px"
         :height "59px"
         :viewBox "0 0 59 59"}
   [:path {:d circle-gray-path
           :fill (gs [:colors :primary-gray-100])
           :stoke :none}]])

(def circle-ring
  [:svg {:class [(:svg-circle classes)
                 (:circle-ring classes)]
         :xmlns "http://www.w3.org/2000/svg"
         :width "34px"
         :height "49px"
         :viewBox "0 0 39 39"}
   [:path {:d circle-ring-path
           :fill :none
           :stroke (gs [:colors :primary-lite-600])
           :stroke-width 1
           :stroke-dasharray 5}]])

(defn circles []
  [:div {:class (:svg-container classes)}
   circle-primary
   circle-gray
   circle-ring])

(defn render
  [{:keys [wait-threshold-seconds]}]
  (let [seconds-waited (reagent/atom 0)
        interval (atom nil)]
    (reagent/create-class
     (cond->
      {:reagent-render
       (fn [{:keys [is-bootstrapped loading-message
                    fixed? fill? show-logo inline]
             :or {loading-message "Please wait while your page loads"
                  is-bootstrapped true
                  show-logo false
                  inline false
                  fixed? false}}]
         (cond->>
          [:<>
           (when show-logo
             [:div {:class (:logo-wrapper classes)}
              [:img {:src c/partner-logo
                     :alt (str c/partner-name " Logo")
                     :class (:logo classes)}]])
           [text/display-10 :p
            {}
            (if (true? is-bootstrapped)
              loading-message
              "Please wait a moment")]
           [circles]

           (when (and (number? wait-threshold-seconds)
                      (> @seconds-waited wait-threshold-seconds))
             [:<>
              [text/body-20 :p {}
               "This seems to be taking longer than usual, try reloading"]
              [btn/secondary-button
               {:style {:margin-top (gs [:spacing :p12])}
                :on-click #(j/call js/location :reload)}
               "Reload"]])]

           (false? inline)
           (conj [:div {:class [(:container classes)
                                (when (true? fixed?)
                                  (:container-fixed classes))
                                (when (true? fill?)
                                  (:container-fill classes))]}])))}
       (number? wait-threshold-seconds)
       (assoc :component-did-mount
              (fn [] (reset! interval (j/call js/window :setInterval
                                              #(swap! seconds-waited inc) 1000)))
              :component-will-unmount
              (fn [] (j/call js/window :clearInterval interval)))))))
